import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EcosystemAPI, LIFECYCLE_STATES, STATE_LABELS, DOMAIN_LABELS, mapServerError, canCreateEntity } from "../../lib/ecosystemApi";
import { LifecycleChip, EnhancedChip } from "../../components/ecosystem/Chips";
import { useAuth } from "../../lib/auth";

export default function Entities() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [state, setState] = useState("");
  const [type, setType] = useState("");
  const [domain, setDomain] = useState("");
  const [enhanced, setEnhanced] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    EcosystemAPI.list().then(setItems).catch((e) => setErr(mapServerError(e)));
  }, []);

  const filtered = useMemo(() => items.filter((i) => {
    if (state && i.lifecycle_state !== state) return false;
    if (type && i.entity_type !== type) return false;
    if (domain && !(i.domain_keys || []).includes(domain)) return false;
    if (enhanced === "enhanced" && !i.safety_classification?.enhanced_review) return false;
    if (enhanced === "standard" && i.safety_classification?.enhanced_review) return false;
    if (mineOnly && i.created_by !== user?.id) return false;
    if (pending && i.lifecycle_state !== "REVIEW") return false;
    return true;
  }), [items, state, type, domain, enhanced, mineOnly, pending, user]);

  return (
    <div className="space-y-4" data-testid="ecosystem-entities">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Entities</h1>
        {canCreateEntity(user) && (
          <Link to="/ecosystem/entities/new" data-testid="ecosystem-new-entity" className="px-3 py-1.5 text-sm rounded bg-slate-900 text-white">New entity</Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded border">
        <select data-testid="ecosystem-filter-state" className="text-sm border rounded p-1" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">All states</option>
          {LIFECYCLE_STATES.map((s) => <option key={s} value={s}>{STATE_LABELS[s]}</option>)}
        </select>
        <select data-testid="ecosystem-filter-type" className="text-sm border rounded p-1" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          {[...new Set(items.map((i) => i.entity_type))].filter(Boolean).map((t) => <option key={t}>{t}</option>)}
        </select>
        <select data-testid="ecosystem-filter-domain" className="text-sm border rounded p-1" value={domain} onChange={(e) => setDomain(e.target.value)}>
          <option value="">All domains</option>
          {Object.entries(DOMAIN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="ecosystem-filter-enhanced" className="text-sm border rounded p-1" value={enhanced} onChange={(e) => setEnhanced(e.target.value)}>
          <option value="">Any tier</option>
          <option value="enhanced">Enhanced only</option>
          <option value="standard">Standard only</option>
        </select>
        <label className="text-sm flex items-center gap-1"><input data-testid="ecosystem-filter-mine" type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} /> Mine only</label>
        <label className="text-sm flex items-center gap-1"><input data-testid="ecosystem-filter-pending" type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} /> In review</label>
      </div>

      {err && <div className="text-sm text-red-700" data-testid="entities-error">{err}</div>}
      {filtered.length === 0 && !err && <div className="text-sm text-slate-500" data-testid="entities-empty">No entities match the current filters.</div>}

      <ul className="space-y-2">
        {filtered.map((e) => (
          <li key={e.id} data-testid={`entity-row-${e.id}`} className="bg-white p-3 rounded border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400 uppercase">{e.entity_type}</span>
              <EnhancedChip classification={e.safety_classification} />
              <Link to={`/ecosystem/entities/${e.id}`} className="text-slate-900 font-medium hover:underline">{e.canonical_name}</Link>
              <LifecycleChip state={e.lifecycle_state} />
              <span className="text-xs text-slate-500">v{e.current_version}</span>
            </div>
            <span className="text-xs text-slate-400">{(e.domain_keys || []).map((d) => DOMAIN_LABELS[d] || d).join(", ")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
